import { App as CapApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { focusManager, type QueryClient } from '@tanstack/react-query';

/** Everything under the orders tree: the board, the summary strip, the badge. */
const ORDERS_TREE = ['orders'];
const RESERVATIONS_LIST = ['reservations', 'list'];
const RESERVATIONS_PENDING = ['reservations', 'pending'];

/**
 * Tell React Query when the app is actually back in front of the operator, and
 * re-read the order and reservation queues the moment it is.
 *
 * Two separate gaps, both proven by the Ryu Wiesbaden blackout (order placed
 * 19:42, seen 21:05):
 *
 * 1. THE SIGNAL. React Query's stock focus manager listens for
 *    `visibilitychange` alone. The dashboard also runs as a Capacitor app,
 *    where iOS suspends the WebView and the resume the native layer is
 *    guaranteed to deliver is `appStateChange` - so on a phone the stock
 *    listener can stay silent across a whole sleep/wake cycle and every
 *    `refetchOnWindowFocus` in the app never fires.
 *
 * 2. THE REACH. Focus refetching only touches ACTIVE queries. She was on
 *    Bookings, where the order board is not mounted, so even a perfect focus
 *    signal leaves that board holding the list from before she pocketed the
 *    phone. Invalidating the orders tree reaches further: the tab-bar badge
 *    (mounted on every screen) refetches at once, and the board is marked
 *    stale so opening it re-reads instead of answering from cache.
 *
 * Safe to call more than once - each call replaces the previous listener.
 */
export function installAppResume(queryClient: QueryClient): void {
  focusManager.setEventListener((handleFocus) => {
    const onActive = (isActive: boolean) => {
      handleFocus(isActive);
      if (!isActive) return;
      void queryClient.invalidateQueries({ queryKey: ORDERS_TREE });
      void queryClient.invalidateQueries({ queryKey: RESERVATIONS_LIST });
      void queryClient.invalidateQueries({ queryKey: RESERVATIONS_PENDING });
    };

    const onVisibility = () => onActive(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibility);

    // `addListener` resolves a tick later, so a teardown can land first.
    let native: PluginListenerHandle | null = null;
    let disposed = false;
    void CapApp.addListener('appStateChange', ({ isActive }) => onActive(isActive)).then(
      (handle) => {
        if (disposed) return void handle.remove();
        native = handle;
      },
    );

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void native?.remove();
      native = null;
    };
  });
}
